import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { NotificationAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';

interface NotificationTemplateModalProps {
    template?: any | null;
    onClose: () => void;
    onSuccess: () => void;
}

export default function NotificationTemplateModal({ template, onClose, onSuccess }: NotificationTemplateModalProps) {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: 'promotions',
        type: 'manual',
        title: '',
        body: '',
        isActive: true,
        variables: [] as Array<{ name: string; description: string; required: boolean; defaultValue: string }>,
        defaultTargeting: {
            targetType: 'all',
            targetCategory: ''
        }
    });

    useEffect(() => {
        if (template) {
            setFormData({
                name: template.name,
                description: template.description || '',
                category: template.category,
                type: template.type,
                title: template.title,
                body: template.body,
                isActive: template.isActive,
                variables: template.variables || [],
                defaultTargeting: template.defaultTargeting || { targetType: 'all', targetCategory: '' }
            });
        }
    }, [template]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = template
                ? await NotificationAPI.templates.update(template._id, formData as any)
                : await NotificationAPI.templates.create(formData as any);

            if (response.success) {
                toast.success(template ? 'Template updated successfully' : 'Template created successfully');
                onSuccess();
            } else {
                toast.error(response.message || 'Failed to save template');
            }
        } catch (err: any) {
            console.error('Error saving template:', err);
            toast.error(err.message || 'Failed to save template');
        } finally {
            setLoading(false);
        }
    };

    const addVariable = () => {
        setFormData({
            ...formData,
            variables: [...formData.variables, { name: '', description: '', required: false, defaultValue: '' }]
        });
    };

    const removeVariable = (index: number) => {
        const newVariables = formData.variables.filter((_, i) => i !== index);
        setFormData({ ...formData, variables: newVariables });
    };

    const updateVariable = (index: number, field: string, value: any) => {
        const newVariables = [...formData.variables];
        newVariables[index] = { ...newVariables[index], [field]: value };
        setFormData({ ...formData, variables: newVariables });
    };

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="max-w-3xl max-h-[90vh] p-0 flex flex-col gap-0 overflow-hidden">
                <div className="p-6 pb-4 border-b shrink-0">
                    <DialogHeader>
                        <DialogTitle>{template ? 'Edit Template' : 'Create Template'}</DialogTitle>
                    </DialogHeader>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    <form id="template-form" onSubmit={handleSubmit} className="space-y-6">
                        {/* Basic Info */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Template Name *</Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="e.g., Welcome Offer"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Category *</Label>
                                <Select
                                    value={formData.category}
                                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select category" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="promotions">Promotions</SelectItem>
                                        <SelectItem value="orderUpdates">Order Updates</SelectItem>
                                        <SelectItem value="deliveryUpdates">Delivery Updates</SelectItem>
                                        <SelectItem value="paymentUpdates">Payment Updates</SelectItem>
                                        <SelectItem value="appUpdates">App Updates</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Internal description for this template"
                                rows={2}
                            />
                        </div>

                        {/* Content */}
                        <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
                            <h4 className="font-medium text-sm">Notification Content</h4>
                            <div className="space-y-2">
                                <Label>Title *</Label>
                                <Input
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="Notification title (can use {{variable}})"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Body *</Label>
                                <Textarea
                                    value={formData.body}
                                    onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                                    placeholder="Notification message body (can use {{variable}})"
                                    rows={3}
                                    required
                                />
                            </div>
                        </div>

                        {/* Variables */}
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <Label>Dynamic Variables</Label>
                                <Button type="button" variant="outline" size="sm" onClick={addVariable}>
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Variable
                                </Button>
                            </div>

                            {formData.variables.length > 0 ? (
                                <div className="space-y-3">
                                    {formData.variables.map((variable, index) => (
                                        <div key={index} className="flex gap-3 items-start border p-3 rounded-md bg-background">
                                            <div className="grid grid-cols-2 gap-3 flex-1">
                                                <Input
                                                    placeholder="Variable Name (e.g. userName)"
                                                    value={variable.name}
                                                    onChange={(e) => updateVariable(index, 'name', e.target.value)}
                                                    className="h-8"
                                                />
                                                <Input
                                                    placeholder="Default Value"
                                                    value={variable.defaultValue}
                                                    onChange={(e) => updateVariable(index, 'defaultValue', e.target.value)}
                                                    className="h-8"
                                                />
                                                <Input
                                                    placeholder="Description"
                                                    value={variable.description}
                                                    onChange={(e) => updateVariable(index, 'description', e.target.value)}
                                                    className="col-span-2 h-8"
                                                />
                                            </div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-destructive"
                                                onClick={() => removeVariable(index)}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-muted-foreground italic">No variables defined. Use {'{{variableName}}'} in content to insert dynamic data.</p>
                            )}
                        </div>

                        {/* Settings */}
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="isActive"
                                checked={formData.isActive}
                                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked as boolean })}
                            />
                            <Label htmlFor="isActive">Active</Label>
                        </div>
                    </form>
                </div>

                <div className="p-6 pt-4 border-t bg-background mt-auto shrink-0">
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" form="template-form" disabled={loading}>
                            {loading ? 'Saving...' : (template ? 'Update Template' : 'Create Template')}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
